// React
import { useEffect, useMemo, useState, useCallback } from 'react'

// Next
import Image from 'next/image'
import Link from 'next/link'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { GetStaticProps } from 'next'

import { GeneratedMeme } from '@/components/memes/templates/generated-meme'
import { AnimatedInput } from '@/components/blocks/animated-input'
import { CustomLoader } from '@/components/blocks/custom-loader'
import { LoginForm } from '@/components/blocks/login-form'
import { Dialog, DialogContent } from '@/components/ui/dialog'

// Utils
import { postData } from '@supermeme-ai/utils'
import {
  useRemainingCredits,
  useSession,
  useRefreshUsedCredits,
  useSubscription,
  useUserDetails,
  useWatermark,
  useUserStats,
} from '@/utils/auth/user-context'
import { shuffleArray } from '@supermeme-ai/utils'
import { getLandingStaticProps } from '@/utils/ui/landing/get-static-props'

// Types
import { Result, TextToMemeGeneratedResponse } from '@supermeme-ai/types'

// External
import { useWindowSize } from 'react-use'
import { Switch } from '@headlessui/react'
import { toast } from 'react-hot-toast'
import { FlameIcon, SparklesIcon } from 'lucide-react'
import { useTranslation } from 'next-i18next'

const TextToMeme: React.FC = () => {
  const { t } = useTranslation('routes/text-to-meme')
  const exampleTexts = t('exampleTexts', { returnObjects: true }) as string[]

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: t('meta.title'),
    description: t('meta.description'),
    applicationCategory: 'Meme Generator',
    operatingSystem: 'Web',
    featureList: [
      'AI-Powered Meme Generation',
      'Template Selection',
      'Custom Image Upload',
      'Caption Regeneration',
    ],
    offers: {
      '@type': 'Offer',
      category: 'Content Creation Tools',
      price: '0',
      priceCurrency: 'USD',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.7',
      ratingCount: '18',
      reviewCount: '18',
      bestRating: '5',
      worstRating: '1',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Supermeme.ai',
      logo: {
        '@type': 'ImageObject',
        url: 'https://supermeme.ai/static/logo-big.png',
      },
    },
    potentialAction: {
      '@type': 'CreateAction',
      object: {
        '@type': 'Thing',
        name: 'Input Text',
      },
      result: {
        '@type': 'ImageObject',
        name: 'Generated Meme',
        contentUrl: 'https://supermeme.ai/static/example-meme.png',
      },
    },
  }

  // context
  const session = useSession()
  const [userDetails] = useUserDetails()
  const stats = useUserStats()
  const subscription = useSubscription()
  const [watermark] = useWatermark()
  const remainingCredits = useRemainingCredits()
  const refreshRemainingCredits = useRefreshUsedCredits()

  // page state
  const [text, setText] = useState<string>('')
  const [results, setResults] = useState<Result[]>([])
  const [loading, setLoading] = useState(false)
  const [searchGIFs, setSearchGIFs] = useState(false)
  const [generateMemesWithoutClick, setGenerateMemesWithoutClick] =
    useState(false)
  const [showLoginDialog, setShowLoginDialog] = useState(false)
  const [shuffledExampleTexts, setShuffledExampleTexts] = useState<string[]>([])

  // other
  const router = useRouter()
  const { width: screenWidth } = useWindowSize()

  const maxDimension = screenWidth >= 500 ? 500 : window.innerWidth - 32

  useEffect(() => {
    setShuffledExampleTexts(shuffleArray([...exampleTexts]))
  }, [])

  useEffect(() => {
    if (!!session) {
      setShowLoginDialog(false)
    }
  }, [session])

  const generateMemes = useCallback(
    async (evt) => {
      evt?.preventDefault()
      if (text) {
        setLoading(true)
        setResults([])
        const { data, error } = await postData<{
          response: TextToMemeGeneratedResponse
        }>(
          '/api/meme/text-to-meme-2',
          {
            text,
            maxDimension,
            inputLanguage: userDetails?.meme_input_language,
            outputLanguage: userDetails?.meme_output_language,
          },
          session?.access_token
        )

        if (error) {
          console.error(error)
          toast.error(t('errors.generation'))
        }

        if (data && data.response) {
          setResults(data.response.results)
          refreshRemainingCredits()
        }

        setLoading(false)
      }
    },
    [
      text,
      maxDimension,
      userDetails,
      session,
      setLoading,
      setResults,
      refreshRemainingCredits,
      t,
    ]
  )

  const generateGIFs = useCallback(
    async (evt) => {
      evt?.preventDefault()
      if (text) {
        setLoading(true)
        setResults([])
        const { data, error } = await postData<{
          response: TextToMemeGeneratedResponse
        }>(
          '/api/meme/text-to-gif',
          {
            text,
            imageCount: 3,
            maxDimension,
            inputLanguage: userDetails?.meme_input_language,
            outputLanguage: userDetails?.meme_output_language,
          },
          session?.access_token
        )

        if (error) {
          toast.error(t('errors.generation'))
        }

        if (data && data.response) {
          setResults(data.response.results)
          refreshRemainingCredits()
        }

        setLoading(false)
      }
    },
    [
      text,
      maxDimension,
      userDetails,
      session,
      setLoading,
      setResults,
      refreshRemainingCredits,
      t,
    ]
  )

  const mobileExampleTexts = useMemo(
    () =>
      shuffledExampleTexts.slice(0, 4).map((text, index) => (
        <div
          key={index}
          className='flex items-center justify-center rounded-lg border border-gray-200 bg-gray-100 p-4 font-semibold'
          onClick={() => {
            setText(text)
            setGenerateMemesWithoutClick(true)
          }}
        >
          <span className='text-sm'>{text}</span>
        </div>
      )),
    [shuffledExampleTexts]
  )

  const desktopExampleTexts = useMemo(
    () =>
      shuffledExampleTexts.slice(0, 6).map((text, index) => (
        <div
          key={index}
          className='flex cursor-pointer items-center justify-center rounded-lg border border-gray-200 bg-gray-100 p-4 text-center hover:shadow-md'
          onClick={() => {
            setText(text)
            setGenerateMemesWithoutClick(true)
          }}
        >
          <span className='text-sm font-semibold'>{text}</span>
        </div>
      )),
    [shuffledExampleTexts]
  )

  useEffect(() => {
    const { prompt } = router.query
    if (prompt) {
      setText(prompt as string)
    }
  }, [router])

  useEffect(() => {
    if (generateMemesWithoutClick) {
      if (!session) {
        setShowLoginDialog(true)
        return
      }

      if (searchGIFs) {
        generateGIFs(null)
      } else {
        generateMemes(null)
      }

      setGenerateMemesWithoutClick(false)
    }
  }, [generateMemesWithoutClick, searchGIFs, generateGIFs, generateMemes])

  return (
    <>
      <Head>
        <title>{t('meta.title')}</title>
        <meta name='description' content={t('meta.description')} />
        <meta property='og:url' content='https://supermeme.ai/text-to-meme' />
        <link rel='canonical' href='https://supermeme.ai/text-to-meme' />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>
      <>
        <div className='mx-auto space-y-10 md:container sm:space-y-8'>
          <form
            onSubmit={(evt) => {
              evt.preventDefault()

              if (!session) {
                setShowLoginDialog(true)
                return
              }

              if (searchGIFs) {
                generateGIFs(evt)
              } else {
                generateMemes(evt)
              }
            }}
          >
            <div className='mx-auto max-w-4xl space-y-4 text-center'>
              <label
                htmlFor='text'
                className='w-full text-center text-xl font-semibold md:text-2xl'
              >
                <span className='block whitespace-nowrap md:inline'>
                  {t('input.label.part1')} {t('input.label.part2')}
                </span>{' '}
                <span className='block whitespace-nowrap md:inline'>
                  <Link
                    href='/search-supported-languages'
                    className='whitespace-nowrap underline'
                  >
                    {t('input.label.part3')}
                  </Link>
                  {t('input.label.part4')}
                </span>
              </label>

              <div className='flex flex-col items-start space-x-0 space-y-2 md:flex-row md:space-x-2 md:space-y-0'>
                <div className='flex w-full flex-col space-y-4'>
                  <AnimatedInput
                    id='text-to-meme'
                    name='text-to-meme'
                    label={t('input.placeholder')}
                    placeholders={exampleTexts}
                    value={text}
                    required
                    maxLength={300}
                    buttonLabel='Generate'
                    buttonIcon={
                      <SparklesIcon
                        className='h-5 w-5 text-white'
                        aria-hidden='true'
                      />
                    }
                    buttonDisabled={
                      !!session && !subscription && remainingCredits <= 0
                    }
                    buttonType='submit'
                    loading={loading}
                    onChange={(value) => setText(value)}
                  />

                  {(!!subscription || remainingCredits > 0) && (
                    <div className='mx-auto flex w-full max-w-2xl justify-between px-1'>
                      <div className='flex w-full justify-between'>
                        <div className='flex items-center justify-center space-x-1 px-2'>
                          <span className='text-xs'>
                            {t('input.toggles.images')}
                          </span>
                          <Switch
                            checked={searchGIFs}
                            onChange={async (value) => {
                              setResults([])
                              setSearchGIFs(value)
                            }}
                            className='relative inline-flex h-[26px] w-[50px] shrink-0 cursor-pointer rounded-full border-2 border-indigo-600 bg-indigo-600 transition-colors duration-200 ease-in-out focus:outline-none'
                          >
                            <span
                              aria-hidden='true'
                              className={`${searchGIFs ? 'translate-x-6' : 'translate-x-0'} pointer-events-none inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow-lg transition duration-200 ease-in-out`}
                            />
                          </Switch>
                          <span className='text-xs'>
                            {t('input.toggles.gifs')}
                          </span>
                        </div>
                        <p className='mt-1 w-[120px] whitespace-nowrap text-right text-xs text-gray-500'>
                          {text?.length ?? 0} {t('input.characterCount')}
                        </p>
                      </div>
                    </div>
                  )}
                  {!!session && !subscription && remainingCredits <= 0 && (
                    <Link
                      className='mt-8 w-full max-w-full space-y-1 text-center text-sm'
                      href='/account/upgrade'
                    >
                      <span
                        className='text-center text-base font-bold text-indigo-600'
                        style={{ flex: 1 }}
                      >
                        {t('noCredits.message')}{' '}
                        <span className='font-bold text-indigo-600 underline'>
                          {t('noCredits.action')}
                        </span>{' '}
                        {t('noCredits.continuation')}
                      </span>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </form>
          {loading && (
            <div className='flex items-center justify-center py-12'>
              <CustomLoader />
            </div>
          )}
          {results.length === 0 && !loading && (
            <>
              <div className='hidden space-y-4 sm:block'>
                <div className='text-center'>
                  <span className='block text-base font-semibold'>
                    {t('exampleTextLabel')}
                  </span>
                </div>
                <div className='mx-auto grid max-w-4xl grid-cols-2 gap-2 sm:grid-cols-3'>
                  {desktopExampleTexts}
                </div>
              </div>

              <div className='block space-y-4 text-center sm:hidden'>
                <span className='block text-lg font-semibold'>
                  {t('exampleTextLabel')}
                </span>

                <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
                  {mobileExampleTexts}
                </div>

                {!!stats && (
                  <div className='flex items-center justify-center'>
                    <Link
                      href='/home'
                      role='group'
                      className='group flex items-center rounded-md px-2 py-1 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 md:text-sm'
                    >
                      <FlameIcon
                        className='mr-3 h-5 w-5'
                        style={{ color: '#f97316' }}
                        fill='#f97316'
                      />
                      <span>
                        {t('currentStreak', {
                          days: stats.currentStreakInDays,
                        })}
                      </span>
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}

          {results.length > 0 && (
            <div className='grid grid-cols-1 gap-2 border-t border-gray-200 pt-6 lg:grid-cols-2 lg:gap-4 xl:grid-cols-2'>
              {searchGIFs && (
                <>
                  <div className='hidden lg:block'></div>
                  <div className='mx-auto lg:mx-0 lg:ml-auto'>
                    <Image
                      priority={false}
                      alt={'Tenor logo'}
                      src={'/static/tenor-logo.png'}
                      width={138.6}
                      height={17.7}
                    />
                  </div>
                </>
              )}
              {results.map((result) => (
                <div key={result.id} className='rounded-lg bg-gray-100 p-4'>
                  <GeneratedMeme
                    initialCaptions={result.captions}
                    memeFormat={searchGIFs ? 'gif' : 'image'}
                    watermark={watermark}
                    initialResultId={result.id}
                    imageSrc={result.image_name}
                    maxDimension={maxDimension}
                    initialHeader={result.header}
                    initialFooter={result.footer}
                    imageWidth={result.width}
                    imageHeight={result.height}
                    isStarredMeme={false}
                    starUnstarCallback={() => {}}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </>
      {/* Login Dialog */}
      <Dialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <DialogContent className='sm:max-w-[425px]'>
          <LoginForm
            isDialog={true}
            redirectUrl={`/text-to-meme?prompt=${text}`}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return getLandingStaticProps({
    locale,
    namespaces: [
      'common',
      'routes/text-to-meme',
      'components/login-form',
      'components/meme-editor',
    ],
  })
}

export default TextToMeme
